import './mermaid.scss'

import { IconButton } from '@chakra-ui/react'
import { mdiCodeBraces, mdiContentCopy, mdiImageOutline } from '@mdi/js'
import Icon from '@mdi/react'
import once from 'lodash/once'
import mermaid from 'mermaid'
import { Dispatch, FC, MouseEventHandler, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'

import useCustomToast from '@/hooks/toasts.tsx'
import useColorModeValue from '@/hooks/useColorModeValue'

interface IMermaidControls {
    children: ReactNode
    isCode: boolean
    isValid: boolean
    setIsCode: Dispatch<SetStateAction<boolean>>
    chart: string
}

const initialize = once(() => {
    mermaid.initialize({
        startOnLoad: true,
        theme: 'neutral',
    })
})

/**
 * I tried various ways to get it to copy to the clipboard as an SVG image
 * but doesn't look like it's possible.
 */
function copySvgToClipboardAsImage(svgCode: string) {
    const svgBase64 = btoa(svgCode)
    const svgUrl = `data:image/svg+xml;base64,${svgBase64}`

    const img = new Image()
    img.setAttribute('crossorigin', 'anonymous')
    img.src = svgUrl

    return new Promise<void>((resolve, reject) => {
        img.onload = () => {
            const canvas = document.createElement('canvas')
            // these come out tiny without the * 10
            canvas.width = img.width * 10
            canvas.height = img.height * 10
            const ctx = canvas.getContext('2d')

            if (ctx) {
                ctx.drawImage(img, 0, 0)

                // Convert the canvas content to a blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        try {
                            const clipboardItem = new ClipboardItem({ 'image/png': blob })
                            navigator.clipboard
                                .write([clipboardItem])
                                .then(() => {
                                    console.log('Image copied to clipboard successfully.')
                                    resolve()
                                })
                                .catch((error) => {
                                    console.error('Error writing to clipboard:', error)
                                    reject(error)
                                })
                        } catch (error) {
                            console.error('Error creating clipboard item:', error)
                            reject(error)
                        }
                    } else {
                        console.error('Blob creation failed.')
                        reject(new Error('Blob creation failed'))
                    }
                }, 'image/png')
            } else {
                console.error('Canvas context is null.')
                reject(new Error('Canvas context is null'))
            }
        }
        img.onerror = (error) => {
            console.error('Image loading failed:', error)
            reject(error)
        }
    })
}

const MermaidControls: FC<IMermaidControls> = ({ children, isCode, isValid, setIsCode, chart }) => {
    const { t } = useTranslation()
    const [addToast] = useCustomToast()
    const containerRef = useRef<HTMLDivElement>(null)

    // canvas.toBlob --> clipboard.write doesn't work on Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const hideCopy = isSafari && !isCode

    const handleClick: MouseEventHandler<HTMLButtonElement> = () => {
        if (!isCode && isValid) {
            const svgCode = containerRef.current?.querySelector('svg')?.outerHTML

            if (svgCode) {
                void copySvgToClipboardAsImage(svgCode).then(() => {
                    addToast('success', t('global.copy.toastMessage'))
                })
            }
        } else {
            const items: Record<string, Blob> = {
                'text/plain': new Blob([chart], { type: 'text/plain' }),
            }
            const htmlCode = containerRef.current?.querySelector('pre')?.outerHTML
            if (htmlCode) {
                items['text/html'] = new Blob([htmlCode], { type: 'text/html' })
            }

            const data: ClipboardItems = [new ClipboardItem(items)]

            void navigator.clipboard.write(data).then(() => {
                addToast('success', t('global.copy.toastMessage'))
            })
        }
    }

    return (
        <div className={`mermaid-chart-container`} ref={containerRef}>
            {children}
            {isValid && (
                <IconButton
                    className="mermaid-control mermaid-control-swap"
                    aria-label={isCode ? 'View code' : 'View image'}
                    rounded="full"
                    onClick={() => setIsCode(!isCode)}
                    variant="copy"
                    mr="12px"
                >
                    <Icon path={isCode ? mdiImageOutline : mdiCodeBraces} size={1} />
                </IconButton>
            )}
            {!hideCopy && (
                <IconButton
                    className="mermaid-control mermaid-control-copy"
                    aria-label="Copy"
                    rounded="full"
                    onClick={handleClick}
                    variant="copy"
                    mr="12px"
                >
                    <Icon path={mdiContentCopy} size={1} />
                </IconButton>
            )}
        </div>
    )
}

const MermaidChart = ({ chart }: { chart: string }) => {
    const [isCode, setIsCode] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const chartRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        initialize()
    }, [])

    useEffect(() => {
        const validateMermaidCode = async () => {
            try {
                const isValid = await mermaid.parse(chart, { suppressErrors: true })
                setIsValid(!!isValid)
            } catch (error) {
                setIsValid(false)
                console.error(error)
            }
        }
        void validateMermaidCode()
    }, [chart])

    useEffect(() => {
        if (!isCode && isValid) {
            const node = chartRef.current
            if (node) {
                node.removeAttribute('data-processed')
                void mermaid.run({
                    nodes: [node],
                })
            }
        }
    }, [isValid, isCode])

    const style = useColorModeValue(oneLight, oneDark)

    return (
        <MermaidControls chart={chart} isValid={isValid} isCode={isCode} setIsCode={setIsCode}>
            {!isCode && isValid ? (
                <div ref={chartRef} className={`mermaid-chart`}>
                    {chart}
                </div>
            ) : (
                <SyntaxHighlighter style={style} language={'mermaid'} PreTag="div">
                    {chart}
                </SyntaxHighlighter>
            )}
        </MermaidControls>
    )
}

export default MermaidChart
